<template>
    <!-- 完整的图表布局容器 -->
    <div class="charts-container">
        <!-- 原始波形图区域 -->
        <div class="original-charts-section">
            <h3 class="section-title">原始波形图</h3>
            <div class="charts-grid">
                <div class="chart-item" ref="waveChartContainers1"></div>
                <div class="chart-item" ref="waveChartContainers2"></div>
            </div>
        </div>
        
        <!-- 周期分析图区域 -->
        <div class="cycle-charts-section" v-if="cycleDataArray.length > 0">
            <h3 class="section-title">周期分析图 ({{ cycleDataArray.length }}个周期)</h3>
            <div class="cycle-charts-grid" :style="{ gridTemplateColumns: 'repeat(' + cycleDataArray.length + ', 1fr)' }">
                <!-- {{ cycleDataArray }} -->
                <div 
                    v-for="(cycleData, index) in cycleDataArray" 
                    :key="index"
                    class="cycle-chart-item" 
                    :ref="'cycleChartContainer' + index"
                    :id="'cycle-chart-' + index"
                >
                    <div class="chart-title">周期 {{ index + 1 }}</div>
                </div>
            </div>
        </div>
        
        <!-- 如果没有数据，显示提示 -->
        <div v-if="!waveData || cycleDataArray.length === 0" class="no-data-tip">
            等待数据加载...
        </div>
    </div>
</template>

<script>
import echarts from "echarts";

export default {
    name: 'waveChart',
    props: {
        id: {
            type: String,
            default: ''
        },
        waveData: {
            type: Object,
            default: null
        }
    },
    data() {
        return {
            // 将单个图表数据改为图表数据数组
            currentPointArray: [],
            voltagePointArray: [],
            chartData: [],
            // 图表实例
            charts: [],
            // 防抖定时器
            resizeTimer: null,
            // 电压-电流坐标点数组
            voltageCurrentPoints: [],
            // 按周期均分后的数据数组
            cycleDataArray: []
        }
    },
    watch: {
        waveData: {
            immediate: true,
            handler(newVal) {
                if (newVal && newVal.currentPointArray && newVal.voltagePointArray) {
                    // 原始数据保持不变（用于原始波形图）
                    this.currentPointArray = newVal.currentPointArray;
                    this.voltagePointArray = newVal.voltagePointArray;
                    this.chartData = [this.currentPointArray, this.voltagePointArray];
                    
                    // 生成电压-电流坐标点数组（电压为横坐标，电流为纵坐标）
                    this.generateVoltageCurrentPoints();
                    
                    // 按周期数均分数据
                    this.splitDataByCycles(newVal.waveSamplingMode.cycleCount);
                    
                    // 数据变化时重新渲染图表
                    this.$nextTick(() => {
                        this.renderCharts();
                    });
                }
            }
        }
    },
    mounted() {
        // 组件挂载后直接渲染图表，不再获取数据
        this.renderCharts();
        // 添加窗口大小变化监听
        window.addEventListener('resize', this.handleResize);
    },
    beforeDestroy() {
        // 组件销毁前移除监听事件
        window.removeEventListener('resize', this.handleResize);
        // 销毁图表实例
        this.charts.forEach(chart => {
            if (chart) {
                chart.dispose();
            }
        });
    },
    methods: {
        
        // 提取数组中的值（去除索引）
        extractValues(dataArray) {
            return dataArray.map(item => {
                // 如果是二维数组 [索引, 值]，返回值部分
                if (Array.isArray(item) && item.length === 2) {
                    return item[1];
                }
                // 否则直接返回
                return item;
            });
        },
        
        // 生成电压-电流坐标点数组（电压为横坐标，电流为纵坐标）
        generateVoltageCurrentPoints() {
            if (this.voltagePointArray.length === 0 || this.currentPointArray.length === 0) {
                this.voltageCurrentPoints = [];
                return;
            }
            
            // 确保两个数组长度一致，取较短的长度
            const minLength = Math.min(this.voltagePointArray.length, this.currentPointArray.length);
            
            this.voltageCurrentPoints = [];
            for (let i = 0; i < minLength; i++) {
                this.voltageCurrentPoints.push({
                    x: this.voltagePointArray[i], // 电压作为横坐标
                    y: this.currentPointArray[i]  // 电流作为纵坐标
                });
            }
            
            // console.log('电压-电流坐标点数组:', this.voltageCurrentPoints);
        },
        
        // 按周期数均分数据
        splitDataByCycles(cycleCount) {
            if (!cycleCount || cycleCount <= 0) {
                this.cycleDataArray = [];
                return;
            }
            
            // 步骤1: 处理 currentPointArray - 除了索引外均分成2个数组，后一半减去前一半
            const currentValues = this.extractValues(this.currentPointArray);
            const halfLength = Math.floor(currentValues.length / 2);
            const currentFirstHalf = currentValues.slice(0, halfLength);
            const currentSecondHalf = currentValues.slice(halfLength, halfLength * 2);
            
            // 后一半减去前一半
            const currentDiffArray = currentSecondHalf.map((val, index) => val - currentFirstHalf[index]);
            // console.log('步骤1 - 电流差值数组:', currentDiffArray);
            
            // 步骤2: 处理 voltagePointArray - 除了索引外取后一半数据
            const voltageValues = this.extractValues(this.voltagePointArray);
            const voltageHalfLength = Math.floor(voltageValues.length / 2);
            const voltageSecondHalf = voltageValues.slice(voltageHalfLength);
            // console.log('步骤2 - 电压后一半数组:', voltageSecondHalf);
            
            // 步骤3: 将电流差值数组按 cycleCount 均分
            const currentPointsPerCycle = Math.floor(currentDiffArray.length / cycleCount);
            const currentCycleArrays = [];
            for (let i = 0; i < cycleCount; i++) {
                const startIdx = i * currentPointsPerCycle;
                const endIdx = (i === cycleCount - 1) ? currentDiffArray.length : (i + 1) * currentPointsPerCycle;
                currentCycleArrays.push(currentDiffArray.slice(startIdx, endIdx));
            }
            // console.log('步骤3 - 电流按周期均分:', currentCycleArrays);
            
            // 步骤2续: 将电压后一半按 cycleCount 均分
            const voltagePointsPerCycle = Math.floor(voltageSecondHalf.length / cycleCount);
            const voltageCycleArrays = [];
            for (let i = 0; i < cycleCount; i++) {
                const startIdx = i * voltagePointsPerCycle;
                const endIdx = (i === cycleCount - 1) ? voltageSecondHalf.length : (i + 1) * voltagePointsPerCycle;
                voltageCycleArrays.push(voltageSecondHalf.slice(startIdx, endIdx));
            }
            // console.log('步骤2续 - 电压按周期均分:', voltageCycleArrays);
            
            // 步骤4: 将电压和电流数组组成二维数组 [电压, 电流]
            this.cycleDataArray = [];
            for (let i = 0; i < cycleCount; i++) {
                const voltageArray = voltageCycleArrays[i];
                const currentArray = currentCycleArrays[i];
                
                // 取两个数组中较短的长度
                const minLen = Math.min(voltageArray.length, currentArray.length);
                const cycleData = [];
                
                for (let j = 0; j < minLen; j++) {
                    cycleData.push([voltageArray[j], currentArray[j]]);
                }
                
                this.cycleDataArray.push(cycleData);
            }
            
            // console.log('步骤4 - 最终周期数据数组:', this.cycleDataArray);
        },

        // 渲染所有图表 - 同时渲染原始图表和周期图表
        async renderCharts() {
            // 如果已有图表实例，先销毁
            this.charts.forEach(chart => {
                if (chart) {
                    chart.dispose();
                }
            });
            this.charts = [];

            // 等待DOM更新完成
            await this.$nextTick();

            // 显示第二个图表容器
            if (this.$refs.waveChartContainers2) {
                this.$refs.waveChartContainers2.style.display = 'block';
            }

            // 渲染电流双Y轴图表
            if (this.$refs.waveChartContainers1 && this.currentPointArray.length > 0) {
                const currentChart = echarts.init(this.$refs.waveChartContainers1);
                const currentOptions = this.createCurrentDualAxisOption();
                currentChart.setOption(currentOptions);
                this.charts.push(currentChart);
            }

            // 渲染电压双Y轴图表
            if (this.$refs.waveChartContainers2 && this.voltagePointArray.length > 0) {
                const voltageChart = echarts.init(this.$refs.waveChartContainers2);
                const voltageOptions = this.createVoltageDualAxisOption();
                voltageChart.setOption(voltageOptions);
                this.charts.push(voltageChart);
            }

            // 为每个周期的数据创建图表（电压-电流二维数组）
            this.cycleDataArray.forEach((cycleData, index) => {
                try {
                    const chartElement = document.getElementById('cycle-chart-' + index);
                    
                    if (chartElement) {
                        if (cycleData.length > 0) {
                            const firstPoint = cycleData[0];
                            if (Array.isArray(firstPoint) && firstPoint.length === 2) {
                                // 创建图表
                                const chart = echarts.init(chartElement);
                                const options = this.createCycleOption(cycleData, index, this.waveData?.waveSamplingMode?.cycleCount);
                                chart.setOption(options);
                                this.charts.push(chart);
                            } else {
                                console.log(`✗ 数据格式错误，应该是 [电压, 电流] 格式`);
                            }
                        } else {
                            console.log(`✗ 周期 ${index + 1} 数据为空`);
                        }
                    } else {
                        console.error(`✗ 周期 ${index + 1} 容器未找到`);
                    }
                } catch (error) {
                    console.error(`✗ 创建周期 ${index + 1} 图表时出错:`, error);
                }
            });
        },

        creatOption(data, index) {
            // 为不同图表设置不同颜色，便于区分
            const colors = [
                "#9c9c00", "#009c9c", "#9c009c", "#9c5000",
                "#00509c", "#50009c", "#9c9c9c", "#509c00",
                "#9c0000", "#009c00", "#00009c", "#9c5050"
            ];

            const option = {
                backgroundColor: "#f5f5f5",
                title: {
                    text: index == 0 ? "电流波形" : "电压波形", // 标题文本
                    left: "center", // 标题水平居中
                    textStyle: {
                        color: "#373b47", // 标题颜色
                        fontSize: 14, // 标题字体大小，稍微调小以适应小屏幕
                        fontWeight: "normal", // 标题字体粗细
                    },
                },
                tooltip: {
                    trigger: 'axis',
                    axisPointer: { type: 'cross' }
                },
                axisPointer: {
                    link: [
                        {
                            xAxisIndex: "all",
                        },
                    ],
                },
                visualMap: [{
                    show: false,
                    type: "continuous",
                    //seriesIndex: index,
                    min: 0,
                    max: 400,
                }],
                grid: {
                    left: '3%',
                    right: '4%',
                    bottom: '15%',
                    top: '15%',
                    containLabel: true
                },
                xAxis: [{
                    name: "",
                    minorTick: {
                        show: true,
                    },
                    minorSplitLine: {
                        show: true,
                    },
                    axisLabel: {
                        color: colors[index], // 修改X轴标签文字颜色为红色
                    },
                    axisLine: {
                        lineStyle: {
                            color: "#373b47", // x 轴的颜色
                        },
                    },
                    splitLine: {
                        show: false,
                    }
                }],
                yAxis: [{
                    type: "value",
                    axisLabel: {
                        color: "#373b47", // 修改X轴标签文字颜色
                    },
                    axisLine: {
                        // 设置Y轴线的样式
                        lineStyle: {
                            color: "#373b47",
                        },
                    },
                    splitLine: {
                        show: false,
                    },
                }],
                dataZoom: [{
                    type: "inside", // 内缩放
                    start: 0, // 初始缩放比例
                    end: 100, // 结束缩放比例 
                }, {
                    type: "slider", // 滚动条
                    show: true, // 是否显示滚动条
                    start: 0, // 初始滚动条位置
                    end: 100, // 结束滚动条位置
                }],
                series: [{
                    name: index == 0 ? "电流" : "电压",
                    type: "line",
                    showSymbol: false,
                    data: data,
                    clip: true,
                    lineStyle: {
                        color: colors[index], // 折线的颜色
                        width: 2,
                    },
                }]
            }

            return option;
        },

        // 创建电流双Y轴图表配置
        createCurrentDualAxisOption() {
            // 计算缩放后的电流数据
            const scaledCurrentData = this.currentPointArray.map(value => {
                if (Array.isArray(value) && value.length === 2) {
                    return [value[0], value[1] / 19554];
                }
                return value / 19554;
            });

            const option = {
                backgroundColor: "#f5f5f5",
                title: {
                    text: "电流波形",
                    left: "center",
                    textStyle: {
                        color: "#373b47",
                        fontSize: 14,
                        fontWeight: "normal"
                    }
                },
                tooltip: {
                    trigger: 'axis',
                    axisPointer: { type: 'cross' }
                },
                legend: {
                    data: [
                        { name: '电流', textStyle: { color: '#e74c3c' } },
                        { name: '实际电流', textStyle: { color: '#3498db' } }
                    ],
                    top: 30,
                    textStyle: {
                        color: "#373b47"
                    },
                    itemGap: 20
                },
                grid: {
                    left: '3%',
                    right: '4%',
                    bottom: '15%',
                    top: '15%',
                    containLabel: true
                },
                xAxis: [{
                    name: "时间",
                    nameGap: 30,
                    minorTick: { show: true },
                    minorSplitLine: { show: true },
                    axisLabel: { color: "#373b47" },
                    axisLine: { lineStyle: { color: "#373b47" } },
                    splitLine: { show: false }
                }],
                yAxis: [
                    {
                        type: "value",
                        name: "电流",
                        nameTextStyle: { color: "#e74c3c" },
                        axisLabel: { 
                            color: "#e74c3c",
                            formatter: '{value}'
                        },
                        axisLine: { 
                            lineStyle: { 
                                color: "#e74c3c"
                            }
                        },
                        splitLine: { show: false }
                    },
                    {
                        type: "value",
                        name: "实际电流",
                        nameTextStyle: { color: "#3498db" },
                        axisLabel: { 
                            color: "#3498db",
                            formatter: '{value}'
                        },
                        axisLine: { 
                            lineStyle: { 
                                color: "#3498db"
                            }
                        },
                        splitLine: { show: false },
                        position: 'right'
                    }
                ],
                dataZoom: [{
                    type: "inside",
                    start: 0,
                    end: 100
                }, {
                    type: "slider",
                    show: true,
                    start: 0,
                    end: 100
                }],
                series: [
                    {
                        name: "电流",
                        type: "line",
                        showSymbol: false,
                        data: this.currentPointArray,
                        clip: true,
                        lineStyle: {
                            color: "#e74c3c",
                            width: 2
                        },
                        itemStyle: {
                            color: "#e74c3c"
                        },
                        yAxisIndex: 0
                    },
                    {
                        name: "实际电流",
                        type: "line",
                        showSymbol: false,
                        data: scaledCurrentData,
                        clip: true,
                        lineStyle: {
                            color: "#3498db",
                            width: 2,
                            type: 'dashed'
                        },
                        itemStyle: {
                            color: "#3498db"
                        },
                        yAxisIndex: 1
                    }
                ]
            };

            return option;
        },

        // 创建电压双Y轴图表配置
        createVoltageDualAxisOption() {
            // 计算缩放后的电压数据
            const scaledVoltageData = this.voltagePointArray.map(value => {
                if (Array.isArray(value) && value.length === 2) {
                    return [value[0], value[1] / 4327];
                }
                return value / 4327;
            });

            const option = {
                backgroundColor: "#f5f5f5",
                title: {
                    text: "电压波形",
                    left: "center",
                    textStyle: {
                        color: "#373b47",
                        fontSize: 14,
                        fontWeight: "normal"
                    }
                },
                tooltip: {
                    trigger: 'axis',
                    axisPointer: { type: 'cross' }
                },
                legend: {
                    data: [
                        { name: '电压', textStyle: { color: '#27ae60' } },
                        { name: '实际电压', textStyle: { color: '#9b59b6' } }
                    ],
                    top: 30,
                    textStyle: {
                        color: "#373b47"
                    },
                    itemGap: 20
                },
                grid: {
                    left: '3%',
                    right: '4%',
                    bottom: '15%',
                    top: '15%',
                    containLabel: true
                },
                xAxis: [{
                    name: "时间",
                    nameGap: 30,
                    minorTick: { show: true },
                    minorSplitLine: { show: true },
                    axisLabel: { color: "#373b47" },
                    axisLine: { lineStyle: { color: "#373b47" } },
                    splitLine: { show: false }
                }],
                yAxis: [
                    {
                        type: "value",
                        name: "电压",
                        nameTextStyle: { color: "#27ae60" },
                        axisLabel: { 
                            color: "#27ae60",
                            formatter: '{value}'
                        },
                        axisLine: { 
                            lineStyle: { 
                                color: "#27ae60"
                            }
                        },
                        splitLine: { show: false }
                    },
                    {
                        type: "value",
                        name: "实际电压",
                        nameTextStyle: { color: "#9b59b6" },
                        axisLabel: { 
                            color: "#9b59b6",
                            formatter: '{value}'
                        },
                        axisLine: { 
                            lineStyle: { 
                                color: "#9b59b6"
                            }
                        },
                        splitLine: { show: false },
                        position: 'right'
                    }
                ],
                dataZoom: [{
                    type: "inside",
                    start: 0,
                    end: 100
                }, {
                    type: "slider",
                    show: true,
                    start: 0,
                    end: 100
                }],
                series: [
                    {
                        name: "电压",
                        type: "line",
                        showSymbol: false,
                        data: this.voltagePointArray,
                        clip: true,
                        lineStyle: {
                            color: "#27ae60",
                            width: 2
                        },
                        itemStyle: {
                            color: "#27ae60"
                        },
                        yAxisIndex: 0
                    },
                    {
                        name: "实际电压",
                        type: "line",
                        showSymbol: false,
                        data: scaledVoltageData,
                        clip: true,
                        lineStyle: {
                            color: "#9b59b6",
                            width: 2,
                            type: 'dashed'
                        },
                        itemStyle: {
                            color: "#9b59b6"
                        },
                        yAxisIndex: 1
                    }
                ]
            };

            return option;
        },

        // 为周期数据创建图表配置（电压-电流二维数组结构）
        createCycleOption(data, cycleIndex, cycleCount) {
            const colors = [
                "#9c9c00", "#009c9c", "#9c009c", "#9c5000",
                "#00509c", "#50009c", "#9c9c9c", "#509c00",
                "#9c0000", "#009c00", "#00009c", "#9c5050"
            ];

            const option = {
                backgroundColor: "#f5f5f5",
                title: {
                    text: `周期 ${cycleIndex + 1} - 伏安图`,
                    left: "center",
                    textStyle: {
                        color: "#373b47",
                        fontSize: 12,
                        fontWeight: "normal"
                    }
                },
                tooltip: {
                    trigger: 'axis',
                    axisPointer: { type: 'cross' },
                    formatter: function(params) {
                        const point = params[0];
                        return `电压: ${point.data[0]}<br/>电流: ${point.data[1]}`;
                    }
                },
                grid: {
                    left: '10%',
                    right: '10%',
                    bottom: '15%',
                    top: '20%',
                    containLabel: true
                },
                xAxis: [{
                    name: "电压",
                    nameLocation: "middle",
                    nameGap: 20,
                    minorTick: { show: true },
                    minorSplitLine: { show: true },
                    axisLabel: { 
                        show: cycleCount <= 3? true : false , // 周期数≤3时显示刻度标签
                        color: "#373b47",
                        interval: 0,
                        margin: 8
                    },
                    axisLine: { lineStyle: { color: "#373b47" } },
                    splitLine: { show: false },
                    axisTick: {
                        show: cycleCount <= 3? true : false, // 周期数≤3时显示刻度线
                        alignWithLabel: true,
                        interval: 0
                    }
                }],
                yAxis: [{
                    name: "电流",
                    nameLocation: "middle",
                    nameGap: 40,
                    nameRotate: 0, // 水平显示文字
                    type: "value",
                    axisLabel: { color: "#373b47" },
                    axisLine: { lineStyle: { color: "#373b47" } },
                    splitLine: { show: false }
                }],
                dataZoom: [{
                    type: "inside",
                    start: 0,
                    end: 100
                }],
                series: [{
                    name: `周期 ${cycleIndex + 1} 电压-电流`,
                    type: "line",
                    showSymbol: false,
                    data: data,
                    clip: true,
                    lineStyle: {
                        color: colors[cycleIndex % colors.length],
                        width: 2
                    }
                }]
            };

            return option;
        },

        // 处理窗口大小变化
        handleResize() {
            // 清除之前的定时器
            if (this.resizeTimer) {
                clearTimeout(this.resizeTimer);
            }
            // 添加防抖，避免频繁resize
            this.resizeTimer = setTimeout(() => {
                // 重新调整所有图表大小
                this.charts.forEach(chart => {
                    if (chart) {
                        chart.resize();
                    }
                });
            }, 300);
        }
    }
}
</script>

<style scoped>
/* 完整的图表容器 */
.charts-container {
    width: 100%;
    display: flex;
    flex-direction: column;
    gap: 32px;
}

/* 区域标题 */
.section-title {
    font-size: 18px;
    font-weight: 600;
    color: #373b47;
    margin: 0 0 16px 0;
    padding-bottom: 8px;
    border-bottom: 2px solid #e8e8e8;
}

/* 原始图表区域 */
.original-charts-section {
    width: 100%;
}

/* 周期图表区域 */
.cycle-charts-section {
    width: 100%;
}

/* 周期图表网格 - CSS Grid实现等宽布局 */
.cycle-charts-grid {
    display: grid;
    gap: 2px;
    width: 100%;
    grid-auto-rows: 250px;
}

/* 周期图表项 - 等宽显示 */
.cycle-chart-item {
    background: #ffffff;
    border-radius: 8px;
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.08);
    border: 1px solid #e8e8e8;
    padding: 10px;
    box-sizing: border-box;
    position: relative;
    min-height: 250px;
}

/* 添加网格布局样式 */
.charts-grid {
    width: 100%;
    display: flex;
    flex-direction: row;
    gap: 24px;
    align-items: flex-start;
    flex-wrap: wrap;
}

/* 单个图表项样式 */
.chart-item {
    height: 320px;
    flex: 1;
    min-width: 400px; /* 设置最小宽度防止挤扁 */
    background: #ffffff;
    border-radius: 8px;
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.08);
    border: 1px solid #e8e8e8;
    padding: 10px;
    transition: all 0.3s ease;
    position: relative;
    width: 100%;
    box-sizing: border-box;
}

/* 图表项悬停效果 */
.chart-item:hover {
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
    border-color: #d0d0d0;
}

/* 图表标题样式 */
.chart-title {
    position: absolute;
    top: 8px;
    left: 50%;
    transform: translateX(-50%);
    font-size: 14px;
    font-weight: 500;
    color: #373b47;
    background: rgba(255, 255, 255, 0.9);
    padding: 4px 12px;
    border-radius: 4px;
    z-index: 10;
}

/* 无数据提示样式 */
.no-data-tip {
    width: 100%;
    height: 200px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 16px;
    color: #999;
    background: #f5f5f5;
    border-radius: 8px;
    border: 2px dashed #ddd;
}

/* 响应式布局优化 */
@media screen and (max-width: 1200px) {
    .charts-grid {
        gap: 16px;
    }
    .chart-item {
        min-width: 350px;
        height: 280px;
    }
    .section-title {
        font-size: 16px;
    }
}

@media screen and (max-width: 768px) {
    .charts-container {
        gap: 24px;
    }
    .charts-grid {
        flex-direction: column;
        gap: 12px;
    }
    .chart-item {
        min-width: 100%;
        height: 250px;
    }
    .section-title {
        font-size: 16px;
        margin-bottom: 12px;
    }
    /* 小屏幕上周期图表调整为垂直堆叠 */
    .cycle-charts-grid {
        gap: 12px;
        grid-auto-rows: 200px;
    }
    .cycle-chart-item {
        min-height: 200px;
    }
}

@media screen and (max-width: 480px) {
    .charts-container {
        gap: 20px;
    }
    .chart-item {
        height: 220px;
        padding: 12px;
    }
    .section-title {
        font-size: 14px;
        padding-bottom: 6px;
    }
    .chart-title {
        font-size: 12px;
        padding: 2px 8px;
    }
    /* 超小屏幕上周期图表调整尺寸 */
    .cycle-charts-grid {
        gap: 10px;
        grid-auto-rows: 180px;
    }
    .cycle-chart-item {
        min-height: 180px;
    }
}
</style>